import React, { useState, useEffect } from 'react';
import CloseIcon from '../icons/CloseIcon';
import InputField from './InputField';
import TextAreaField from './TextAreaField';
import Button from './Button';

const pxToRem = (value: number) => `${value / 16}rem`;

interface AddNewPointTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    title: string;
    description: string;
    pointAmount: number;
  }) => Promise<void> | void;
}

const AddNewPointTemplateDialog: React.FC<AddNewPointTemplateDialogProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [pointAmount, setPointAmount] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // 다이얼로그가 닫힐 때 폼 초기화
      setTitle('');
      setDescription('');
      setPointAmount('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!title.trim() || !description.trim() || !pointAmount.trim()) {
      return;
    }

    const pointValue = parseInt(pointAmount);
    if (isNaN(pointValue) || pointValue <= 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim(),
        pointAmount: pointValue
      });
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSaveDisabled = !title.trim() || !description.trim() || !pointAmount.trim() || isSubmitting;

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        fontFamily: 'Pretendard, sans-serif'
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: pxToRem(450), // 394 + 28*2 = 450
          background: 'var(--Color-background-normal, white)',
          borderRadius: pxToRem(16),
          boxShadow: '0px 0px 3px 0px rgba(0, 0, 0, 0.06), 0px 8px 16px 0px rgba(0, 0, 0, 0.12)',
          padding: `${pxToRem(24)} ${pxToRem(28)}`,
          display: 'flex',
          flexDirection: 'column',
          gap: pxToRem(40),
          maxHeight: '90vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 메인 컨텐츠 영역 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: pxToRem(40),
          width: '100%',
          flex: '1 1 0'
        }}>
          {/* 헤더 */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: pxToRem(8),
            width: '100%'
          }}>
            <div style={{
              flex: '1 1 0',
              display: 'flex',
              flexDirection: 'column',
              gap: pxToRem(2)
            }}>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: pxToRem(20),
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: '600',
                lineHeight: pxToRem(30),
                letterSpacing: '-0.02em'
              }}>
                특별 포인트 템플릿 추가하기
              </div>
              <div style={{
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: pxToRem(16),
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: '500',
                lineHeight: pxToRem(24)
              }}>
                포인트 템플릿을 생성하여 특별 포인트를 지급해 보세요.
              </div>
            </div>
            <div
              onClick={onClose}
              style={{
                padding: pxToRem(4),
                borderRadius: pxToRem(8),
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                flexShrink: 0
              }}
            >
              <CloseIcon width={24} height={24} color="var(--Color-text-normal, #161616)" />
            </div>
          </div>

          {/* 입력 필드 영역 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: pxToRem(24),
            width: '100%'
          }}>
            {/* 포인트 제목 및 내용 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: pxToRem(12),
              width: '100%'
            }}>
              {/* 포인트 제목 */}
              <div style={{
                height: pxToRem(48),
                width: '100%'
              }}>
                <InputField
                  value={title}
                  onChange={setTitle}
                  placeholder="포인트 제목을 입력해 주세요"
                  width="100%"
                  height={pxToRem(48)}
                  size="small"
                />
              </div>

              {/* 포인트 내용 */}
              <div style={{
                height: pxToRem(84),
                width: '100%'
              }}>
                <TextAreaField
                  value={description}
                  onChange={setDescription}
                  placeholder="포인트 내용을 입력해 주세요."
                  width="100%"
                  height={pxToRem(84)}
                  size="small"
                />
              </div>
            </div>

            {/* 지급 포인트 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: pxToRem(12),
              width: '100%'
            }}>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: pxToRem(16),
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: '600',
                lineHeight: pxToRem(24)
              }}>
                지급 포인트
              </div>
              <div style={{
                height: pxToRem(48),
                width: '100%'
              }}>
                <InputField
                  value={pointAmount}
                  onChange={setPointAmount}
                  placeholder="포인트를 입력해주세요"
                  width="100%"
                  height={pxToRem(48)}
                  size="small"
                  type="number"
                />
              </div>
            </div>
          </div>
        </div>

        {/* 버튼 영역 */}
        <div style={{
          display: 'flex',
          gap: pxToRem(8),
          width: '100%'
        }}>
          <Button
            width={pxToRem(124)}
            height={pxToRem(48)}
            variant="cancel"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              backgroundColor: '#E9E9E9',
              color: 'var(--Color-text-normal, #161616)',
              fontSize: pxToRem(14),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '500',
              lineHeight: pxToRem(21)
            }}
          >
            취소
          </Button>
          <Button
            width={pxToRem(262)}
            height={pxToRem(48)}
            variant="submit"
            onClick={handleSave}
            disabled={isSaveDisabled}
            style={{
              backgroundColor: 'var(--Color-fill-normal, #293044)',
              color: 'var(--Color-text-inverse, white)',
              fontSize: pxToRem(14),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '500',
              lineHeight: pxToRem(21)
            }}
          >
            {isSubmitting ? '처리 중...' : '템플릿 추가'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddNewPointTemplateDialog;

